/**
 * Helper Functions - Formatters
 * Utility functions for formatting data
 * Following PROJECT_STRUCTURE_GUIDE.md pattern
 */

/**
 * Format a number with thousand separators
 */
export const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('en-US').format(value);
};

/**
 * Format bytes to human-readable size
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Truncate string with ellipsis
 */
export const truncateString = (str: string, maxLength: number): string => {
  if (str.length <= maxLength) return str;
  return str.substring(0, maxLength) + '...';
};

/**
 * Convert array of objects to CSV string
 * @param data - Array of objects to convert
 * @param headers - Optional array of header names (defaults to object keys)
 * @returns CSV string
 */
export const convertToCSV = (
  data: Array<Record<string, unknown>>,
  headers?: string[]
): string => {
  // Get headers from first object if not provided
  const csvHeaders = headers || (data.length > 0 ? Object.keys(data[0]) : []);

  // Return empty string if no headers available
  if (csvHeaders.length === 0) {
    return '';
  }

  // Escape CSV values (handle commas, quotes, newlines)
  const escapeCSVValue = (value: unknown): string => {
    if (value === null || value === undefined) {
      return '';
    }
    const stringValue = String(value);
    // If value contains comma, quote, or newline, wrap in quotes and escape quotes
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  // Create CSV rows
  const csvRows = [
    csvHeaders.map(escapeCSVValue).join(','), // Header row
    ...data.map((row) =>
      csvHeaders.map((header) => escapeCSVValue(row[header])).join(',')
    ),
  ];

  return csvRows.join('\n');
};

/**
 * Download CSV file
 * @param csvContent - CSV string content
 * @param filename - Name of the file to download
 */
export const downloadCSV = (csvContent: string, filename: string): void => {
  // Add BOM for UTF-8 to support special characters in Excel
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};